import { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../../components/ui/alert-dialog';
import { Button } from '../../../../../components/ui/button';
import { DeleteSessionConfirmDialog } from '../../../../sessions/components/DeleteSessionConfirmDialog';
import { cancelPendingSessionAutoSave } from '../../../../sessions/services/sessionAutoSave';
import { useSessionStore } from '../../../../sessions/store/sessionStore';
import { formatBytes, getPersistedSessionsStorageBytes } from '../../../../sessions/utils/sessionDisplayUtils';
import { SessionsSettingsRow } from './SessionsSettingsRow';

export function ProSessionsSection() {
  const { currentSession, getSavedSessions, deleteSession, deleteAllSessions } = useSessionStore();

  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const sessions = getSavedSessions();

  const storageLabel = useMemo(() => {
    return formatBytes(getPersistedSessionsStorageBytes());
  }, [sessions.length]);

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteSession(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="space-y-3" data-testid="pro-sessions-section">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-sm font-medium">Saved Sessions</div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span data-testid="sessions-total-count">
              {sessions.length} session{sessions.length === 1 ? '' : 's'}
            </span>
            <span data-testid="sessions-storage-used">{storageLabel}</span>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => setShowDeleteAllConfirm(true)}
          disabled={sessions.length === 0}
          data-testid="delete-all-sessions-button"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete All
        </Button>
      </div>

      {sessions.length === 0 ? (
        <div
          className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
          data-testid="no-saved-sessions-info"
        >
          No saved sessions.
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((session) => (
            <SessionsSettingsRow
              key={session.id}
              session={session}
              isCurrent={session.id === currentSession?.id}
              onRequestDelete={() => setPendingDelete({ id: session.id, name: session.name })}
            />
          ))}
        </div>
      )}

      <DeleteSessionConfirmDialog
        open={pendingDelete !== null}
        sessionName={pendingDelete?.name ?? ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <AlertDialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
        <AlertDialogContent data-testid="delete-all-sessions-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all sessions?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            This deletes all saved sessions and clears the current session snapshot. This action cannot be undone.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-all-sessions-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                cancelPendingSessionAutoSave();
                deleteAllSessions();
                setShowDeleteAllConfirm(false);
              }}
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              data-testid="delete-all-sessions-confirm"
            >
              Delete All Sessions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
